import {
  type AccountAndAsset,
  AssetType,
  type Business,
  type ChildAccount,
  type Debt,
  FDItemType,
  type Income,
  type InheritanceAndTrust,
  type RealEstate,
  type Vehicle,
} from "@prisma/client-generated";

/**
 * FD item types that participate in the required-fields system. Legacy
 * joint property is intentionally excluded: it is a read-only carryover
 * surface (no edit modal, no add affordance) and its rows are allowed to
 * have missing fields. Excluding it at the type level makes it a compile
 * error to add required fields for `LEGACY_JOINT`
 */
export type FDRequiredFieldsItemType = Exclude<
  FDItemType,
  typeof FDItemType.LEGACY_JOINT
>;

/**
 * Per-`FDItemType` mapping to the canonical Prisma model. Used as a
 * compile-time guard so `REQUIRED_FIELDS_BY_ITEM_TYPE` entries can only
 * list real field names on the matching model.
 */
interface FDItemTypeToModel {
  [FDItemType.ACCOUNTS]: AccountAndAsset;
  [FDItemType.ASSETS]: AccountAndAsset;
  [FDItemType.CHILD_ACCOUNTS]: ChildAccount;
  [FDItemType.VEHICLES]: Vehicle;
  [FDItemType.INCOME]: Income;
  [FDItemType.REAL_ESTATE]: RealEstate;
  [FDItemType.BUSINESS]: Business;
  [FDItemType.DEBTS]: Debt;
  [FDItemType.INHERITANCE]: InheritanceAndTrust;
  [FDItemType.TRUSTS]: InheritanceAndTrust;
}

export type RequiredFDField<T extends FDRequiredFieldsItemType> = Extract<
  keyof FDItemTypeToModel[T],
  string
>;

type RequiredFieldsMap = {
  [T in FDRequiredFieldsItemType]: readonly RequiredFDField<T>[];
};

/**
 * Single source of truth for per-FD-item-type required fields, shared
 * between the API and the web client. The `satisfies` clause enforces
 * that every field name is a real key on the matching Prisma model.
 */
export const REQUIRED_FIELDS_BY_ITEM_TYPE: RequiredFieldsMap = {
  [FDItemType.ACCOUNTS]: [
    "accountType",
    "institution",
    "accountNumber",
    "valueInCents",
  ],
  [FDItemType.ASSETS]: ["assetType", "valueInCents"],
  [FDItemType.CHILD_ACCOUNTS]: [
    "childName",
    "childAccountType",
    "institution",
    "accountNumber",
    "valueInCents",
  ],
  [FDItemType.VEHICLES]: [
    "vehicleType",
    "make",
    "model",
    "purchaseYear",
    "purchasePriceInCents",
  ],
  [FDItemType.INCOME]: ["incomeType", "year", "valueInCents"],
  [FDItemType.REAL_ESTATE]: [
    "streetAddress",
    "city",
    "state",
    "zipcode",
    "country",
    "titleHeldInNameOf",
    "purchasePriceInCents",
    "purchaseYear",
    "currentAssessedValueInCents",
    "lastAssessmentYear",
  ],
  [FDItemType.BUSINESS]: [
    "name",
    "businessType",
    "ownershipPercentage",
    "valueInCents",
  ],
  [FDItemType.DEBTS]: ["creditor", "yearIncurred", "amountDueInCents"],
  [FDItemType.INHERITANCE]: ["nameOfTrust", "valueInCents"],
  [FDItemType.TRUSTS]: ["nameOfTrust", "valueInCents"],
};

/**
 * Asset subtypes that represent physical property (vs. financial accounts).
 * Account-based assets additionally require `institution` and `accountNumber`.
 */
const PHYSICAL_ASSET_TYPES: readonly AssetType[] = [
  AssetType.JEWELRY,
  AssetType.FIREARMS,
  AssetType.COLLECTIONS,
  AssetType.TOOLS_EQUIPMENT,
  AssetType.CROPS_LIVESTOCK,
  AssetType.HOME_FURNISHINGS,
  AssetType.ARTS_AND_ANTIQUES,
  AssetType.CONTENT_OF_SAFE_OR_SAFE_DEPOSIT_BOX,
  AssetType.INTELECTUAL_PROPERTY_INTEREST,
  AssetType.JUDGEMENTS_LIENS,
  AssetType.OTHER,
];

export function isAccountBasedAsset(
  assetType: string | undefined | null,
): boolean {
  if (!assetType) return false;
  return !(PHYSICAL_ASSET_TYPES as readonly string[]).includes(assetType);
}

/**
 * Required-field list for an ASSET, adding `institution` /
 * `accountNumber` when the asset is account-based.
 */
export function getAssetRequiredFields(item: {
  readonly assetType?: string | null | undefined;
}): readonly RequiredFDField<typeof FDItemType.ASSETS>[] {
  const base = REQUIRED_FIELDS_BY_ITEM_TYPE[FDItemType.ASSETS];
  return isAccountBasedAsset(item.assetType ?? undefined)
    ? [...base] // Remove institution and accountNumber temporarily to unblock prenup customers
    : base;
}

/**
 * A value is "missing" when it is `null`, `undefined`, NaN, or an
 * empty/whitespace-only string. Other falsy values (`0`, `false`) count
 * as present.
 */
export function isMissingRequiredValue(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (typeof value === "number") return Number.isNaN(value);
  return false;
}

/**
 * Static required-field list by `FDItemType`. For ASSETS, callers that
 * may see account-based assets must compose with `getAssetRequiredFields`.
 *
 * Constrained to `FDRequiredFieldsItemType` so legacy joint can't be
 * asked for required fields — it has none by design.
 */
export function getRequiredFieldsForItem<I extends FDRequiredFieldsItemType>(
  itemType: I,
): readonly RequiredFDField<I>[] {
  return REQUIRED_FIELDS_BY_ITEM_TYPE[itemType];
}

/**
 * Narrowing guard so callers can route LEGACY_JOINT around the required
 * fields system without duplicating the exclusion list.
 */
export function hasRequiredFieldsCheck(
  itemType: FDItemType,
): itemType is FDRequiredFieldsItemType {
  return itemType !== FDItemType.LEGACY_JOINT;
}
